import type { EChartsOption } from 'echarts'
import type { TooltipComponentFormatterCallbackParams } from 'echarts'
import type { ParsedTable, ChartGeneratorSettings } from '../types'
import {
  toNumber,
  axisPercentFormatter,
  tooltipPercentFormatter,
  resolveSeriesColors,
  GeneratorError,
  getChartColorByIndex,
  chartLegend,
  CHART_GRID_TOP_WITH_LEGEND,
  uniqueCategoryNames,
  cleanCategoryName,
} from './helpers';
import { orderRows } from '../utils/chartSort';

/**
 * Generates ECharts options for a horizontal Bar chart
 * Focuses on a single selected column (e.g., a specific time period)
 *
 * Assumes:
 * - The first column contains the category names (Y-axis)
 * - The `settings.selectedHeader` specifies which column contains the data to plot
 *
 * @param t - The parsed table data
 * @param title - The title for the chart
 * @param settings - Settings with `selectedHeader` for the data column
 * @returns ECharts option configuration object for a bar chart
 * @throws {GeneratorError} If the table structure or settings are unsuitable
 */
export function barOptions(
  t: ParsedTable,
  title: string,
  settings?: ChartGeneratorSettings
): EChartsOption {
  const selectedHeader = settings?.selectedHeader

  if (!selectedHeader) {
    throw new GeneratorError(
      'Bar chart requires a `selectedHeader` in settings to choose the data column.'
    )
  }

  if (t.colCnt < 2 || t.rowCnt === 0) {
    throw new GeneratorError(
      'Bar chart requires at least 2 columns (category + data) and 1 data row.'
    )
  }

  const headerIndex = t.headers.findIndex((h) => h === selectedHeader)

  if (headerIndex === -1) {
    const headerList = t.headers.join(', ')
    throw new GeneratorError(
      `Selected header "${selectedHeader}" not found in table headers: [${headerList}]`
    )
  }

  if (headerIndex === 0) {
    throw new GeneratorError(
      "Cannot use the first column header for Bar chart data; it's assumed to be the category label."
    )
  }

  // Extract and sort data. Rows are picked by POSITION when the caller
  // sends positions (#1808) — a name matches every row carrying it — and by
  // name otherwise, as before.
  const sortOrder = settings?.sortOrder
  const rowPositions = settings?.selectedRowPositions
  const numericData = t.rows
    .filter((row, rowIndex) => {
      if (rowPositions && rowPositions.length > 0) return rowPositions.includes(rowIndex)
      if (settings?.selectedRows && settings.selectedRows.length > 0) {
        const categoryName = row[0] || 'Unnamed Category'
        return settings.selectedRows.includes(categoryName)
      }
      return true
    })
    .map((row) => {
      const categoryName = row[0] || 'Unnamed Category'
      const value = headerIndex < row.length ? toNumber(row[headerIndex]) : null
      return {
        name: categoryName,
        value: value,
      }
    })
    .filter((item) => item.value !== null && !isNaN(item.value))
  // No sort_order: the pre-#1808 order, untouched. With one: reading order
  // via the shared helper (drawn top-down by `yAxis.inverse`).
  const chartData = sortOrder
    ? orderRows(numericData, (item) => item.value, sortOrder)
    : numericData
      .sort((a, b) => (a.value ?? -Infinity) - (b.value ?? -Infinity))

  if (chartData.length === 0) {
    throw new GeneratorError(
      `No valid numeric data found for the selected header "${selectedHeader}".`
    )
  }

  const categoryNames = chartData.map((item) => item.name)
  // Repeated labels stay separate bars (#1808); unique ones are unchanged.
  const categories = uniqueCategoryNames(categoryNames)

  // Build data values with per-bar colors/gradients
  // Uses deterministic color generation for unknown codes to ensure consistency
  const dataValues = chartData.map((item, index) => {
    const key = item.name.toLowerCase();
    // Prefer gradient if available, then colorMap, then index-based fallback
    const gradient = settings?.gradientMap?.[key];
    const color = settings?.colorMap?.[key] || getChartColorByIndex(index);

    return {
      value: item.value,
      itemStyle: { color: gradient || color },
    }
  })

  return {
    title: {
      text: title,
      left: 'left',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      // `trigger: 'item'`, not 'axis' (#968 comment 4). An axis trigger always
      // hands the formatter an array, which the single-bar formatter below
      // bails out of — so the tooltip rendered as an empty box. It also has
      // nothing to add here: one bar sits at each axis position.
      trigger: 'item',
      confine: true,
      formatter: (params: TooltipComponentFormatterCallbackParams) => {
        if (Array.isArray(params)) {
          return ''
        }
        const p = params as any
        const value = typeof p.value === 'number' ? p.value : null
        const formattedValue =
          value !== null
            ? settings?.dataType === 'freq'
              ? value.toLocaleString()
              : `${(value * 100).toFixed(1)}%`
            : 'N/A'
        return `${cleanCategoryName(p.name)}<br/>${p.seriesName}: ${formattedValue}`
      },
    },
    // Default legend naming the plotted column (#1172): a single-series bar
    // has nothing else that says WHICH column it shows, and the Chart Design
    // legend controls need an existing legend to act on — the design patch
    // only creates one from a stored override, which an untouched chart
    // never has.
    legend: chartLegend([selectedHeader]),
    grid: {
      left: '3%',
      right: '10%',
      top: CHART_GRID_TOP_WITH_LEGEND,
      bottom: '15%',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      axisLabel: {
        formatter: settings?.dataType === 'freq' ? undefined : axisPercentFormatter,
      },
    },
    yAxis: {
      type: 'category',
      data: categories,
      // A set sort_order is READING order, top-down (#1808). Without one
      // the axis keeps ECharts' bottom-up order, which the ascending
      // legacy sort relies on to put the biggest bar on top.
      ...(sortOrder ? { inverse: true } : {}),
      axisLabel: {
        // interval deliberately unset (#1178): ECharts' own 'auto' thins
        // colliding labels — 25 months no longer smear into one band. The
        // old force-every-label look is the "Every label" setting.
        fontSize: 10,
        formatter: function (raw: string) {
          const value = cleanCategoryName(raw)
          return value.length > 25 ? value.substring(0, 25) + '...' : value
        },
      },
    },
    series: [
      {
        name: settings?.selectedHeader || 'Value',
        type: 'bar',
        data: dataValues,
        barWidth: '40%',
        label: {
          show: true,
          position: 'right',
          formatter: (params) => {
            const value = typeof params.value === 'number' ? params.value : null
            return settings?.dataType === 'freq'
              ? value?.toLocaleString() || 'N/A'
              : tooltipPercentFormatter(value)
          },
          color: '#333',
          fontSize: 9,
        },
        emphasis: {
          focus: 'series',
        },
      },
    ],
    color: resolveSeriesColors(categoryNames, settings?.colorMap),
  }
}
